import { useEffect, useState, useCallback, useMemo, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Skeleton } from "@mui/material";
import { TopBarContext } from "../../providers/TopBarProvider";
import CustomNoRows from "../atoms/CustomNoRows";
import DispatchService from "../../services/DispatchService";
import DispatchItem from "../molecules/DispatchItem";
import { ToastContext } from "../../providers/ToastProvider";
import OrderService from "../../services/OrderService";

const Dispatches = () => {
  const [orderId, setOrderId] = useState(undefined);
  const [orderStatus, setOrderStatus] = useState(-1);
  const [companyName, setCompanyName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [dispatches, setDispatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const { setTitle, setSearchOptions, setBtnPrimary } =
    useContext(TopBarContext);
  const { setShowToast, setToastMessage } = useContext(ToastContext);
  const dispatchService = useMemo(() => new DispatchService(), []);
  const orderService = useMemo(() => new OrderService(), []);
  const rowsPerPage = 25;
  const location = useLocation();
  const navigate = useNavigate();

  const getAll = useCallback(
    async (orderId) => {
      try {
        setIsLoading(true);
        setDispatches([]);
        const resOrder = await orderService.get(orderId);
        const orderData = resOrder.data.result;
        setOrderStatus(orderData.status);
        setCompanyName(orderData.companyName);
        setBranchName(orderData.branchName);

        const res = await dispatchService.getAll(orderId);
        const items = res.data.result;

        if (items.length === 0) {
          return;
        }

        setDispatches([...items]);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatchService, setDispatches, orderService, setOrderStatus]
  );

  const handleComplete = async (orderId, dispatchId) => {
    try {
      if (orderStatus === 3) {
        return;
      }

      await dispatchService.complete(orderId, dispatchId);

      setToastMessage([
        {
          severity: "success",
          primary: `Despacho #${dispatchId} completado exitosamente`,
        },
      ]);
    } catch (error) {
      setToastMessage([
        {
          severity: "error",
          primary: `No se pudo completar el despacho #${dispatchId}`,
        },
      ]);
    } finally {
      await getAll(orderId);
      setShowToast(true);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const localOrderId = searchParams.get("id");

    setOrderId(localOrderId);

    getAll(localOrderId);
  }, [location, getAll, setOrderId]);

  useEffect(() => {
    const titleText = companyName && branchName 
      ? `Despachos - ${companyName} - ${branchName} (#${orderId})`
      : `Despachos de Orden #${orderId}`;
    setTitle(titleText);
    setSearchOptions({
      action: () => null,
      hidden: true,
    });
    setBtnPrimary({
      action: null,
    });
  }, [setTitle, setSearchOptions, setBtnPrimary, orderId, orderStatus, companyName, branchName]);

  const classes = {
    root: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, 335px)",
      gridGap: 20,
      justifyContent: "center",
      my: 8,
    },
    skeleton: {
      width: "100%",
      height: 311,
    },
    customNoRows: {
      gridColumn: "6 span",
    },
    pagination: {
      width: "fit-content",
      margin: "0 auto",
    },
  };

  return (
    <>
      <Box sx={classes.root}>
        {isLoading ? (
          new Array(rowsPerPage)
            .fill(1)
            .map((_, idx) => (
              <Skeleton
                animation="wave"
                key={`skeleton-${idx}`}
                variant="rounded"
                sx={classes.skeleton}
              />
            ))
        ) : dispatches.length === 0 ? (
          <Box sx={classes.customNoRows}>
            <CustomNoRows
              message={
                error
                  ? "Error intentando obtener los despachos"
                  : "No hay despachos registrados"
              }
            />
          </Box>
        ) : (
          dispatches.map((dispatch) => {
            const dispatchId = dispatch.dispatchId;

            return (
              <DispatchItem
                key={`dispatch-${dispatchId}`}
                reference={String(dispatchId)}
                quantity={String(dispatch.packageCount)}
                status={dispatch.status}
                orderStatus={orderStatus}
                handleOpen={() =>
                  navigate(`/orders/dispatches/dispatch?id=${dispatchId}`)
                }
                handleDelete={null}
                handleComplete={() => handleComplete(orderId, dispatchId)}
              />
            );
          })
        )}
      </Box>
    </>
  );
};

export default Dispatches;
